/**
 * MessagingInbox — bandeja de mensajes con hilos, filtros y respuesta.
 * Usado tanto por Super Admin (recibe todo lo dirigido a super_admin) como
 * por Institutional Admin (ve sus propios hilos y respuestas del super).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Mail, MailOpen, ArrowUp, Loader2 } from "lucide-react";
import MessageComposer, { CATEGORIES, PRIORITIES } from "./MessageComposer";

const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));
const PRIO_LABEL = Object.fromEntries(PRIORITIES.map((p) => [p.value, p.label]));

const PRIO_STYLE = {
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  urgent: "bg-red-50 text-red-700 border-red-200",
};

export default function MessagingInbox({ currentUserRole }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [filter, setFilter] = useState("all"); // all | unread

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages`, { withCredentials: true });
      setThreads(res.data.threads || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la bandeja");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = useCallback(async (messageId) => {
    try {
      await axios.patch(`${API}/messages/${messageId}/read`, {}, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  }, [load]);

  const filteredThreads = useMemo(() => {
    if (filter !== "unread") return threads;
    return threads.filter((t) =>
      t.some((m) => !m.read_by_recipient && (
        (currentUserRole === "super_admin" && m.to_role === "super_admin")
        || (currentUserRole === "institutional_admin" && m.to_role === "institutional_admin")
      )),
    );
  }, [threads, filter, currentUserRole]);

  const active = filteredThreads[activeIdx] || null;
  const activeHead = active?.[0];
  const activeLast = active?.[active.length - 1];

  const unreadInThread = (thread) => thread.filter((m) =>
    !m.read_by_recipient && (
      (currentUserRole === "super_admin" && m.to_role === "super_admin")
      || (currentUserRole === "institutional_admin" && m.to_role === "institutional_admin")
    ),
  ).length;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[500px]" data-testid="messaging-inbox">
      {/* Lista de hilos */}
      <div className="md:col-span-1 border border-gray-100 rounded-2xl overflow-hidden bg-white">
        <div className="flex items-center justify-between p-3 border-b border-gray-100">
          <h3 className="font-bold text-sm">Bandeja</h3>
          <div className="flex gap-1">
            <Button
              size="sm" variant={filter === "all" ? "default" : "outline"}
              onClick={() => setFilter("all")} data-testid="filter-all"
            >Todos</Button>
            <Button
              size="sm" variant={filter === "unread" ? "default" : "outline"}
              onClick={() => setFilter("unread")} data-testid="filter-unread"
            >Sin leer</Button>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="refresh-inbox">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <div className="divide-y divide-gray-100 max-h-[520px] overflow-y-auto">
          {filteredThreads.length === 0 && (
            <p className="text-sm text-gray-400 p-6 text-center">No hay mensajes.</p>
          )}
          {filteredThreads.map((thread, idx) => {
            const head = thread[0];
            const last = thread[thread.length - 1];
            const unread = unreadInThread(thread);
            const isActive = idx === activeIdx;
            return (
              <button
                key={head.thread_id}
                onClick={() => setActiveIdx(idx)}
                className={`w-full text-left p-3 hover:bg-gray-50 transition ${isActive ? "bg-blue-50" : ""}`}
                data-testid={`thread-item-${idx}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm ${unread ? "font-bold text-gray-900" : "font-medium text-gray-700"} truncate`}>
                    {head.subject}
                  </p>
                  {unread > 0 && <Badge className="bg-red-500 text-white text-[10px]">{unread}</Badge>}
                </div>
                <p className="text-xs text-gray-500 truncate mt-1">
                  {head.from_name} · {thread.length} mensaje{thread.length > 1 ? "s" : ""}
                </p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${PRIO_STYLE[head.priority] || PRIO_STYLE.medium}`}>
                    {PRIO_LABEL[head.priority] || head.priority}
                  </span>
                  <span className="text-[10px] text-gray-500 px-1.5 py-0.5">
                    {new Date(last.created_at).toLocaleDateString("es")}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Hilo activo */}
      <div className="md:col-span-2 border border-gray-100 rounded-2xl bg-white flex flex-col">
        {!active && (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm p-6">
            Selecciona un hilo para leerlo.
          </div>
        )}
        {active && (
          <>
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{CAT_LABEL[activeHead.category] || activeHead.category}</p>
              <h2 className="text-lg font-bold text-gray-900 mt-1">{activeHead.subject}</h2>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full border ${PRIO_STYLE[activeHead.priority] || PRIO_STYLE.medium}`}>
                  {PRIO_LABEL[activeHead.priority] || activeHead.priority}
                </span>
                <span className="text-xs text-gray-500">
                  Iniciado por <b>{activeHead.from_name}</b> el {new Date(activeHead.created_at).toLocaleString("es")}
                </span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[380px]">
              {active.map((m) => {
                const isMine = (currentUserRole === "super_admin" && m.from_role === "super_admin")
                  || (currentUserRole === "institutional_admin" && m.from_role === "institutional_admin");
                const isForMe = (currentUserRole === "super_admin" && m.to_role === "super_admin")
                  || (currentUserRole === "institutional_admin" && m.to_role === "institutional_admin");
                return (
                  <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${isMine ? "bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white" : "bg-gray-100 text-gray-800"}`}>
                      <p className="text-xs opacity-75 mb-1">{m.from_name} · {new Date(m.created_at).toLocaleString("es")}</p>
                      <p className="text-sm whitespace-pre-wrap">{m.body}</p>

                      {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                        <div
                          className={`mt-3 grid gap-2 ${m.attachments.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}
                          data-testid={`msg-attachments-${m.id}`}
                        >
                          {m.attachments.map((a) => {
                            const src = `data:${a.mime_type};base64,${a.data}`;
                            return (
                              <a
                                key={a.id}
                                href={src}
                                target="_blank"
                                rel="noreferrer"
                                download={a.filename}
                                className="block rounded-lg overflow-hidden border border-white/20 bg-black/5 hover:opacity-90 transition"
                                title={a.filename}
                              >
                                <img
                                  src={src}
                                  alt={a.filename}
                                  className="w-full h-32 object-cover"
                                />
                              </a>
                            );
                          })}
                        </div>
                      )}

                      {isForMe && !m.read_by_recipient && (
                        <button
                          onClick={() => markRead(m.id)}
                          className="text-xs mt-2 underline opacity-75 hover:opacity-100"
                          data-testid={`mark-read-${m.id}`}
                        >
                          <MailOpen className="w-3 h-3 inline mr-1" /> Marcar como leído
                        </button>
                      )}
                      {isForMe && m.read_by_recipient && (
                        <p className="text-xs mt-2 opacity-60"><Mail className="w-3 h-3 inline mr-1" /> Leído</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t border-gray-100 flex justify-end">
              <MessageComposer
                triggerLabel="Responder"
                parentId={activeLast.id}
                toUserId={currentUserRole === "super_admin" ? activeHead.from_user_id : null}
                defaultSubject={activeHead.subject.startsWith("Re:") ? activeHead.subject : `Re: ${activeHead.subject}`}
                onSent={load}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
